import React, { useState } from "react";
import {
  Button,
  Typography,
  Space,
  Switch,
  Modal,
  Slider,
  message,
  Divider,
  Alert,
} from "antd";
import {
  SettingOutlined,
  SoundOutlined,
  SoundFilled,
  NotificationOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { notificationSound } from "../../utils/notificationSound";

const { Text, Title } = Typography;

interface NotificationSettingsProps {
  visible: boolean;
  soundEnabled: boolean;
  volume: number;
  onClose: () => void;
  onSoundToggle: (enabled: boolean) => void;
  onVolumeChange: (value: number) => void;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  visible,
  soundEnabled,
  volume,
  onClose,
  onSoundToggle,
  onVolumeChange,
}) => {
  const [persistentSound, setPersistentSound] = useState(() =>
    notificationSound.isPersistent()
  );
  const [isTestingSound, setIsTestingSound] = useState(false);

  const handleTestSound = async () => {
    setIsTestingSound(true);
    try {
      await notificationSound.forcePlaySound();
      message.info({
        content: "Enhanced notification sound played! 🔊",
        duration: 3,
      });
    } catch (error) {
      message.error("Failed to play test sound");
    } finally {
      setIsTestingSound(false);
    }
  };

  const handlePersistentToggle = (enabled: boolean) => {
    setPersistentSound(enabled);
    notificationSound.setPersistent(enabled);
    localStorage.setItem("notificationSoundPersistent", enabled.toString());

    message.info({
      content: enabled
        ? "Persistent sound enabled - notifications will repeat for better attention"
        : "Persistent sound disabled - single play only",
      duration: 4,
    });
  };

  const handleRequestNotificationPermission = () => {
    if ("Notification" in window) {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          message.success("Browser notifications enabled successfully!");
          // Show a test notification
          notificationSound.showBrowserNotification(
            "🎉 Notifications Enabled!",
            "You'll now receive desktop notifications even when the browser is in the background",
            "/favicon.ico"
          );
        } else if (permission === "denied") {
          message.error(
            "Browser notifications denied. Please check your browser settings."
          );
        } else {
          message.warning("Browser notifications permission pending.");
        }
      });
    } else {
      message.error("Your browser doesn't support desktop notifications.");
    }
  };

  const getNotificationPermissionStatus = () => {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission;
  };

  const getNotificationStatusText = () => {
    const status = getNotificationPermissionStatus();
    switch (status) {
      case "granted":
        return "✅ Enabled";
      case "denied":
        return "❌ Denied";
      case "default":
        return "⏳ Not Set";
      case "unsupported":
        return "❌ Unsupported";
      default:
        return "❓ Unknown";
    }
  };

  const getNotificationStatusColor = () => {
    const status = getNotificationPermissionStatus();
    switch (status) {
      case "granted":
        return "success";
      case "denied":
        return "error";
      case "default":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <Title level={4} className="mb-0">
            Notification Settings
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="test"
          icon={<ExperimentOutlined />}
          onClick={handleTestSound}
          loading={isTestingSound}
          type="dashed"
        >
          Test Enhanced Sound
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Done
        </Button>,
      ]}
      width={600}
      className="notification-settings-modal"
    >
      <div className="space-y-6">
        {/* Sound Settings */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {soundEnabled ? (
                <SoundFilled className="text-blue-500 text-lg" />
              ) : (
                <SoundOutlined className="text-lg" />
              )}
              <div>
                <Text strong className="text-base">
                  Sound Notifications
                </Text>
                <div className="text-sm text-gray-500 mt-1">
                  Play enhanced audio alerts when new notifications arrive
                </div>
              </div>
            </div>
            <Switch
              checked={soundEnabled}
              onChange={onSoundToggle}
              size="default"
            />
          </div>
        </div>

        {/* Enhanced Settings - Only show when sound is enabled */}
        {soundEnabled && (
          <>
            <Divider />

            {/* Persistent Sound Control */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <NotificationOutlined className="text-orange-500 text-lg" />
                  <div>
                    <Text strong className="text-base">
                      Persistent Alerts
                    </Text>
                    <div className="text-sm text-gray-500 mt-1">
                      Repeat notification sound for critical attention
                    </div>
                  </div>
                </div>
                <Switch
                  checked={persistentSound}
                  onChange={handlePersistentToggle}
                  size="default"
                />
              </div>
            </div>

            <Divider />

            {/* Volume Control */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Text strong className="text-base">
                  Volume Level
                </Text>
                <div className="flex items-center space-x-2">
                  <Text type="secondary" className="text-sm">
                    {Math.round(volume)}%
                  </Text>
                  <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-200"
                      style={{ width: `${volume}%` }}
                    />
                  </div>
                </div>
              </div>

              <Slider
                min={0}
                max={100}
                value={volume}
                onChange={onVolumeChange}
                marks={{
                  0: { label: "🔇", style: { fontSize: "16px" } },
                  25: "Quiet",
                  50: "Medium",
                  75: "Loud",
                  100: { label: "🔊", style: { fontSize: "16px" } },
                }}
                tooltip={{
                  formatter: (value) => `${value}%`,
                }}
              />

              <div className="mt-3 flex space-x-2">
                <Button
                  size="small"
                  onClick={handleTestSound}
                  loading={isTestingSound}
                  icon={<SoundOutlined />}
                >
                  Test Sound
                </Button>
                <Text type="secondary" className="text-xs self-center">
                  Test the enhanced notification sound at current volume
                </Text>
              </div>
            </div>

            <Divider />
          </>
        )}

        {/* Browser Notifications */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="text-lg">🖥️</div>
              <div>
                <Text strong className="text-base">
                  Desktop Notifications
                </Text>
                <div className="text-sm text-gray-500 mt-1">
                  Show notifications even when browser is in background
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Text
                type={getNotificationStatusColor() as any}
                className="text-sm font-medium"
              >
                {getNotificationStatusText()}
              </Text>
              <Button
                size="small"
                type={
                  getNotificationPermissionStatus() === "granted"
                    ? "default"
                    : "primary"
                }
                onClick={handleRequestNotificationPermission}
                disabled={getNotificationPermissionStatus() === "unsupported"}
              >
                {getNotificationPermissionStatus() === "granted"
                  ? "Test"
                  : "Enable"}
              </Button>
            </div>
          </div>

          {getNotificationPermissionStatus() === "denied" && (
            <Alert
              message="Browser notifications are blocked"
              description="To enable desktop notifications, please click the lock icon in your browser's address bar and allow notifications for this site."
              type="error"
              showIcon
              className="mt-2"
            />
          )}
        </div>

        <Divider />

        {/* Auto Refresh Info */}
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <div className="text-lg">🔄</div>
            <Text strong className="text-base">
              Auto Refresh
            </Text>
          </div>
          <div className="ml-8">
            <Text type="secondary" className="text-sm">
              Notifications are automatically checked every 10 seconds to ensure
              you receive the latest updates in real-time.
            </Text>
          </div>
        </div>

        {/* Sound Details */}
        {/* <div className="bg-gray-50 p-4 rounded-lg">
          <Text strong className="text-sm text-gray-700">
            Enhanced Sound Features:
          </Text>
          <ul className="mt-2 text-sm text-gray-600 space-y-1">
            <li>• 2.5-second duration (vs. previous 0.4 seconds)</li>
            <li>• Multi-tone attention-grabbing pattern</li>
            <li>• Three-phase sound sequence for urgency</li>
            <li>• Optional repeat playback for persistence</li>
            <li>• Optimized for maximum browser compatibility</li>
          </ul>
        </div> */}
      </div>
    </Modal>
  );
};

export default NotificationSettings;
